import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import { Subject } from 'rxjs';
import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ReleaseReportService } from '../services/releasereport.service';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ReleaseReport } from '../models/release-report';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { state, style, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastService } from '../services/toast.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UserProfileService } from '../services/user-profile.service';
import { LoadingService } from '../services/loading.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-releasereport',
  templateUrl: './releasereport.component.html',
  styleUrls: ['./releasereport.component.css', '../home/home.component.css'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' })
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
  providers: [DatePipe],
  standalone: false,
})
export class ReleaseReportComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  releases = new Array<ReleaseReport>();
  releaseDates: Array<string> = [];
  friendlyDates: Array<string> = [];
  selectedDate: string = '';
  endDate: string = '';
  dateRangeForm: FormGroup;

  displayedColumns: string[] = [
    'select',
    'repo',
    'team',
    'branch',
    'workItemId',
    'title',
    'uatState',
    'state',
    'type',
  ];
  columns: any[] = [];
  selectedReleases: ReleaseReport[] = [];
  selection = new SelectionModel<ReleaseReport>(true, []);
  initialSelection: any = [];
  allowMultiSelect: boolean = true;
  dataSource!: MatTableDataSource<ReleaseReport>;
  searchTerm: string = '';
  expandedRow: ReleaseReport | null = null;
  allRowsSelected: boolean = false;
  downloadHref!: SafeHtml;
  environment: Environment;
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('releasesTable') releasesTable!: MatTable<any>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  constructor(
    @Inject(EnvironmentService) private readonly environmentService: EnvironmentService,
    @Inject(ReleaseReportService) private readonly releaseReportService: ReleaseReportService,
    @Inject(UserProfileService) private readonly userProfileService: UserProfileService,
    @Inject(ToastService) private readonly toast: ToastService,
    @Inject(DomSanitizer) private readonly sanitizer: DomSanitizer,
    @Inject(DatePipe) private readonly datePipe: DatePipe,
    @Inject(FormBuilder) private readonly fb: FormBuilder,
    public readonly loadingService: LoadingService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.dateRangeForm = this.fb.group({
      start: [null],
      end: [null],
    });
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<ReleaseReport>([]);
    this.selectedDate = '';
    this.columns = this.getColumns();
    this.selection.changed.subscribe(() => {
      this.allRowsSelected = this.isAllSelected();
    });
    this.getReleaseDates();
    this.getReleaseReport('', '');
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  clearDates(): void {
    this.selectedDate = '';
    this.endDate = '';
    this.getReleaseReport(this.selectedDate, this.endDate);
  }

  getReleaseDates(): void {
    this.releaseReportService
      .getReleaseDates()
      .subscribe((response: Array<string>) => {
        if (response) {
          this.releaseDates.push(...response);
          this.releaseDates.forEach((date) => {
            this.friendlyDates.push(this.getFriendlyDate(date));
          });
        }
      });
  }

  get reportTitle(): string {
    const reportTitleFormat = 'EEE. M/d/yyyy';
    if (this.endDate) {
      const formattedStartDate = this.datePipe.transform(
        this.selectedDate,
        reportTitleFormat
      );
      const formattedEndDate = this.datePipe.transform(
        this.endDate,
        reportTitleFormat
      );
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
    return 'Pending';
  }

  getReleaseReport(startDateString: string, endDateString: string): void {
    let startDate = '';
    let endDate = '';
    this.releases = new Array<ReleaseReport>();
    this.loadingService.show('releaseReport');

    if (
      startDateString &&
      startDateString != '' &&
      endDateString &&
      endDateString !== ''
    ) {
      let newDate = new Date(startDateString);
      this.selectedDate = startDateString;
      startDate = this.formatDate(newDate);
      let newEndDate = new Date(endDateString);
      this.endDate = endDateString;
      endDate = this.formatDate(newEndDate);
    }

    this.releaseReportService
      .getReleaseReport(startDate, endDate)
      .pipe(
        finalize(() => this.loadingService.hide('releaseReport'))
      )
      .subscribe((releases: any) => {
        this.releases = this.processReleases(releases);
        this.dataSource.data = this.releases;
        this.selection.clear();
      });
  }

  processReleases(releases: any[]): ReleaseReport[] {
    const processedReleases: ReleaseReport[] = [];
    releases?.forEach((release: any) => {
      try {
        processedReleases.push({
          repo: release.Repository?.RepoName,
          team: this.getTeam(release.WorkItem?.AreaPath),
          branch: release.Commit?.BranchName,
          workItemId: release.WorkItem?.WorkItemID,
          title: release.WorkItem?.Title,
          uatState: this.getUatState(release),
          state: release.WorkItem?.State,
          type: release.WorkItem?.WorkItemType,
          areaPath: release.WorkItem?.AreaPath,
          uatCompletedBy: release.WorkItem?.UATCompletedBy,
          qaCompletedBy: release.WorkItem?.QACompletedBy,
          featureId: release.Feature?.FeatureID,
          featureLink: release.Feature?.Link,
          epicId: release.Epic?.EpicID,
          epicLink: release.Epic?.Link,
          iterationPath: release.WorkItem?.IterationPath,
          assignedTo: release.WorkItem?.AssignedTo?.split('<')[0] ?? '',
          commitId: release.Commit?.CommitID,
          featureBusinessValue: this.extractText(
            release.Feature?.BusinessValueStatement
          ),
          epicBusinessValue: this.extractText(
            release.Epic?.BusinessValueStatement
          ),
        });
      } catch (error) {
        console.error(error);
      }
    });
    return processedReleases;
  }

  getTeam(areaPath: string): string {
    if (!areaPath) {
      console.error('Area path is null or whitespace');
      return '';
    }
    let team = areaPath.split('\\');
    return team[team.length - 1];
  }

  getUatState(release: any): string {
    const qaCompletedBy = release.WorkItem?.QACompletedBy;
    const featureId = release.Feature?.FeatureID;
    return !featureId || this.isNullOrWhitespace(qaCompletedBy)
      ? 'Not Finished'
      : 'Finished';
  }

  applySearchFilter() {
    this.dataSource.filter = this.searchTerm;
  }

  toggleRow(release: ReleaseReport) {
    if (this.expandedRow === release) {
      this.expandedRow = null;
    } else {
      this.expandedRow = release;
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected == numRows;
  }

  selectAllRows() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  extractText(html?: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html.toString();
    return div.textContent ?? div.innerText ?? '';
  }

  getColumns(): any[] {
    let columns = [
      {
        columnDef: 'repo',
        header: 'Repo',
        cell: (release: ReleaseReport) => `${release.repo}`,
      },
      {
        columnDef: 'team',
        header: 'Team',
        cell: (release: ReleaseReport) => `${release.team}`,
      },
      {
        columnDef: 'branch',
        header: 'Branch',
        cell: (release: ReleaseReport) => `${release.branch}`,
      },
      {
        columnDef: 'workItemId',
        header: 'WI ID',
        cell: (release: ReleaseReport) => `${release.workItemId}`,
      },
      {
        columnDef: 'title',
        header: 'Title',
        cell: (release: ReleaseReport) => `${release.title}`,
      },
      {
        columnDef: 'uatState',
        header: 'QAT/UAT',
        cell: (release: ReleaseReport) => `${release.uatState}`,
      },
      {
        columnDef: 'state',
        header: 'State',
        cell: (release: ReleaseReport) => `${release.state}`,
      },
      {
        columnDef: 'type',
        header: 'Type',
        cell: (release: ReleaseReport) => `${release.type}`,
      },
    ];
    return columns;
  }

  isNullOrWhitespace(input: string) {
    return !input?.trim();
  }

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    return date.toLocaleDateString('en-US', options);
  }

  getFriendlyDate(unfriendlyDateString: string): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    const theDate: Date = new Date(unfriendlyDateString);
    return theDate.toLocaleDateString('en-US', options);
  }

  copyReleases() {
    let selectedReleases = this.getSelectedReleases();
    let copiedText = this.generateCopiedText(selectedReleases);
    navigator.clipboard.writeText(copiedText);
    this.toast.notification(`Copied ${selectedReleases.length} rows to clipboard`);
  }

  getColumnHeaders(): string {
    return this.columns
      .filter((column) => column.columnDef !== 'select')
      .map((column) => column.header)
      .join('\t');
  }

  generateCopiedText(selectedReleases: any[]): string {
    let copiedText = 'Release Report\n\n' + this.getColumnHeaders() + '\n';

    selectedReleases.forEach((release) => {
      const releaseData = this.columns
        .filter((column) => column.columnDef !== 'select')
        .map((column) => release[column.columnDef])
        .join('\t');

      copiedText += releaseData + '\n';
    });

    return copiedText;
  }

  exportCsv() {
    let selectedReleases = this.getSelectedReleases();
    let headers = this.columns
      .filter((column) => column.columnDef !== 'select')
      .map((column) => column.header)
      .join(',');

    let csvContent = headers + '\n';

    selectedReleases.forEach((release: { [x: string]: any }) => {
      let releaseData = this.columns
        .filter((column) => column.columnDef !== 'select')
        .map((column) => release[column.columnDef])
        .join(',');

      csvContent += releaseData + '\n';
    });

    let uri = this.sanitizer.bypassSecurityTrustUrl(
      'data:text/json;charset=UTF-8,' + encodeURIComponent(csvContent)
    );
    this.downloadHref = uri;
  }

  exportPdf(shouldPrint: boolean) {
    let selectedReleases = this.getSelectedReleases();
    let headerNames = [
      'Repo',
      'Team',
      'Branch',
      'WI ID',
      'Title',
      'QAT/UAT',
      'State',
      'Type',
    ];
    let array = this.mapSelectedReleases(selectedReleases);
    let doc = this.createPdfDocument();

    autoTable(doc, {
      head: [headerNames],
      body: array,
      rowPageBreak: 'avoid',
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 15 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 40 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 25 },
      },
    });

    if (shouldPrint) {
      doc.autoPrint();
      doc.output('pdfobjectnewwindow');
    } else {
      doc.save('Release Report.pdf');
    }
  }

  createPdfDocument(): jsPDF {
    const doc = new jsPDF();
    doc.text('Release Report', 105, 10, { align: 'center' });
    return doc;
  }

  getSelectedReleases() {
    let selectedReleases = this.selection.selected;
    if (selectedReleases.length === 0) {
      selectedReleases = this.dataSource.data;
    }
    return selectedReleases;
  }

  mapSelectedReleases(releases: ReleaseReport[]) {
    return releases.map((release) => [
      release.repo,
      release.team,
      release.branch,
      release.workItemId.toString(),
      release.title,
      release.uatState,
      release.state,
      release.type,
    ]);
  }
}
